// src/pages/member/MemberResourcesGuidance.jsx
import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const pageBg = "#f9fbfe";
const sectionHeaderBg = "#c4d8ec";   // light blue bar
const sectionHeaderBorder = "#4e81c4"; // dark blue strip
const linkBlue = "#005695";

const consumerProtectionItems = [
  {
    title: "Guidance on the Consumer Rights Act 2015 (pdf)",
    description:
      "The Consumer Rights Act 2015 sets out the rights of consumers when they buy goods, services and digital content, to ensure goods and services are of good quality, match what was promised and are provided fairly. It gives consumers clear remedies if these standards are not met.",
    href: "/assets/guidance/consumer-rights-act-2015.pdf",
  },
  {
    title: "Guidance on the Consumer Contracts Regulations 2013 (pdf)",
    description:
      "These regulations aim to protect consumers by outlining information that must be provided before a contract is agreed, granting a cancellation period and by regulating additional charges. They also regulate delivery times, refunds, and when businesses can complete services within the cancellation period.",
    href: "/assets/guidance/consumer-contracts-regulations-2013.pdf",
  },
  {
    title:
      "Guidance on the Alternative Dispute Resolution Regulations 2015 (pdf)",
    description:
      "These regulations require traders to inform consumers about an approved ADR body when a dispute cannot be resolved internally.",
    href: "/assets/guidance/alternative-dispute-resolution-regulations-2015.pdf",
  },
  {
    title: "Guidance on the General Data Protection Regulations (pdf)",
    description:
      "These regulations set out rules for how personal data must be collected, used, and stored.",
    href: "/assets/guidance/general-data-protection-regulations.pdf",
  },
];

const workingWithConsumersItems = [
  {
    title: "Performance Prediction Assumptions (pdf)",
    description:
      "This guidance sets out reliable assumptions and sources you can use in system performance calculations to ensure your customers are given accurate, trustworthy information.",
    href: "/assets/guidance/performance-prediction-assumptions.pdf",
  },
  {
    title: "Consumers in Vulnerable Circumstances (pdf)",
    description:
      "Helping you understand how to identify and make reasonable adjustments for consumers with additional needs or who find themselves in vulnerable circumstances - a great resource to help train your team.",
    href: "/assets/guidance/consumers-in-vulnerable-circumstances.pdf",
  },
  {
    title: "Checklist for Members visiting Consumers' Homes (pdf)",
    description:
      "A helpful checklist setting out what you should do and look out for when visiting your customers in their homes.",
    href: "/assets/guidance/checklist-members-visiting-consumers-homes.pdf",
  },
  {
    title: "Member FAQs - Withholding Payment (pdf)",
    description:
      "This guidance explains what to do if a customer doesn’t pay what they owe under a contract and outlines your responsibilities, their rights, and what’s expected in resolving the issue.",
    href: "/assets/guidance/member-faqs-withholding-payment.pdf",
  },
  {
    title: "Best practice storage guidance (pdf)",
    description:
      "This guidance explains what information to give your customers about battery systems, how to present it clearly, and how to set out the potential financial benefits in proposals.",
    href: "/assets/guidance/best-practice-storage-guidance.pdf",
  },
];

function GuidanceSection({ title, items }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid #dde4f0",
        mb: 4,
      }}
    >
      <Box
        sx={{
          backgroundColor: sectionHeaderBg,
          borderTop: `4px solid ${sectionHeaderBorder}`,
          px: 3,
          py: 1.5,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "#1d2736" }}
        >
          {title}
        </Typography>
      </Box>

      <Box sx={{ p: 3, backgroundColor: "#ffffff" }}>
        <List disablePadding>
          {items.map((item, index) => (
            <React.Fragment key={item.title}>
              <ListItemButton
                component="a"
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  alignItems: "flex-start",
                  px: 0,
                  "&:hover .linkText": { textDecoration: "underline" },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 48,
                    mt: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      backgroundColor: sectionHeaderBorder,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PictureAsPdfIcon sx={{ fontSize: 18, color: "#fff" }} />
                  </Box>
                </ListItemIcon>

                <ListItemText
                  primary={
                    <Typography
                      className="linkText"
                      sx={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: linkBlue,
                      }}
                    >
                      {item.title}
                    </Typography>
                  }
                  secondary={
                    item.description && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                          color: "#4a4f59",
                          lineHeight: 1.5,
                        }}
                      >
                        {item.description}
                      </Typography>
                    )
                  }
                />
              </ListItemButton>

              {index < items.length - 1 && (
                <Divider sx={{ my: 1.75, borderColor: "#edf1f7" }} />
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>
    </Paper>
  );
}

export default function MemberResourcesGuidance() {
  return (
    <Box sx={{ backgroundColor: pageBg, minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        {/* Breadcrumb */}
        <Typography
          variant="body2"
          sx={{ color: "#6b7280", mb: 1 }}
        >
          Home &gt; Member Resources &gt; Guidance
        </Typography>

        {/* Page title + intro */}
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 1, color: "#111827" }}
        >
          Guidance
        </Typography>
        <Typography
          variant="body1"
          sx={{ mb: 4, color: "#4b5563" }}
        >
          We have prepared guidance to help your consumers comply with the Code.
        </Typography>

        {/* Sections */}
        <GuidanceSection
          title="Consumer protection legislation"
          items={consumerProtectionItems}
        />

        <GuidanceSection
          title="Working with your Consumers"
          items={workingWithConsumersItems}
        />
      </Container>
    </Box>
  );
}
